import { useTranslation } from 'react-i18next';
import { Box, Flex, Image } from '@mafc/trolly';
import { FC, useState } from 'react';

import COLORS from 'theme/colors';
import { isUndefined } from 'utils/helpers.util';
import { IVoucherCardVariant, VoucherBadgeVariant } from './VoucherCard.constants';
import * as S from './VoucherCard.style';
import { ReactComponent as CaretUp } from 'assets/svgs/caretup.svg';
import { ReactComponent as CaretDown } from 'assets/svgs/caretdown.svg';
import { CheckCircle } from '@mafc/icon';

const VoucherCard: FC<{
  bottomMessage: { __html: string };
  code: string;
  description: { __html: string };
  expiringIn?: string;
  title: string;
  isApplied: boolean;
  isApplicable: boolean;
  termsAndConditions: Array<{ __html: string }>;
  variant: IVoucherCardVariant;
  imageUrl?: string[];
  usagePerCustomer?: number;
  redeemedCount?: number;
  onVoucherApply: (code: string) => void;
  onToggleCard?: (data: { isExpanded: boolean; voucherCode: string }) => void;
}> = ({
  bottomMessage,
  code,
  description,
  expiringIn,
  isApplicable,
  isApplied,
  title,
  termsAndConditions,
  variant,
  imageUrl = [],
  usagePerCustomer,
  redeemedCount,
  onVoucherApply,
  onToggleCard,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setExpanded] = useState(false);

  const toggleExpanded = () => {
    setExpanded(state => !state);
    if (onToggleCard) {
      onToggleCard({ isExpanded, voucherCode: code });
    }
  };

  const shouldShowRedemptionCount = !isUndefined(usagePerCustomer) && !isUndefined(redeemedCount);

  const bothBadgesExists = expiringIn && shouldShowRedemptionCount;
  return (
    <S.VoucherCard variant={variant} isApplied={isApplied} data-testid={`voucher-card-${code}`}>
      <S.VoucherCardContent width={1}>
        <S.VoucherRow>
          <Box>
            <Flex alignItems="center">
              <Flex flexDirection="column">
                {imageUrl.length > 0 && (
                  <S.ImagesWrapper>
                    {imageUrl.map((image: string) => (
                      <Box mb={1} key={image}>
                        <Image name={title} url={image} height="32px" width="52px" />
                      </Box>
                    ))}
                  </S.ImagesWrapper>
                )}
              </Flex>
              <S.Title>{title}</S.Title>
            </Flex>
            <S.Description dangerouslySetInnerHTML={description} />
          </Box>

          <Flex flexDirection="column" columnnGap={2}>
            {shouldShowRedemptionCount && (
              <S.Badge
                bg={COLORS.PERSIA_GREEN}
                color={COLORS.WHITE}
                variant={
                  bothBadgesExists ? VoucherBadgeVariant.BottomFlat : VoucherBadgeVariant.Default
                }
              >
                {t('promo.redeemedOf', {
                  usagePerCustomer,
                  redeemedCount,
                })}
              </S.Badge>
            )}

            {!!expiringIn && (
              <S.Badge
                bg={COLORS.ERROR_RED_50}
                color={COLORS.ERROR_RED_700}
                variant={
                  bothBadgesExists ? VoucherBadgeVariant.TopFlat : VoucherBadgeVariant.Default
                }
              >
                {expiringIn}
              </S.Badge>
            )}
          </Flex>
        </S.VoucherRow>

        <S.VoucherRow height="32px">
          <S.TnCLink onClick={toggleExpanded} variant="link">
            {t('promo.termsAndConditions')}

            {isExpanded ? <CaretUp /> : <CaretDown />}
          </S.TnCLink>
        </S.VoucherRow>

        {isExpanded && (
          <Box mb={3}>
            <S.VoucherCode>{code}</S.VoucherCode>
            {termsAndConditions.map(tnc => (
              <S.TnCRow key={tnc} dangerouslySetInnerHTML={tnc} />
            ))}
          </Box>
        )}
      </S.VoucherCardContent>

      <S.BottomMessage variant={variant} isApplied={isApplied}>
        <Box dangerouslySetInnerHTML={bottomMessage} />
        {isApplied ? (
          <S.CodeApplied>
            <CheckCircle stroke="3" size="XS" fill={COLORS.PERSIAN_GREEN} />
            {t('promo.applied')}
          </S.CodeApplied>
        ) : (
          <S.ApplyButton
            onClick={onVoucherApply.bind(null, code)}
            variant="custom"
            preventTextTransform
            disabled={!isApplicable}
          >
            {t('promo.apply')}
          </S.ApplyButton>
        )}
      </S.BottomMessage>
    </S.VoucherCard>
  );
};

export default VoucherCard;
